/**
 * Dependency engine for the company-portal permissions matrix (PROLINE-2589 AC).
 *
 * `applyPermissionDependencies(permissions)` takes the admin's explicit clicks
 * and derives the effective (rendered/saved) state plus which flags were
 * "forced" (set by a rule, not by the admin — these render checked+disabled).
 */
import { companyPermissionModules } from "../../../../utils/Constants";
import { ALL_MODULES, isLevelEnabled } from "./permissionModules";

const FLAGS = ["can_view", "can_add", "can_edit", "can_delete"];

/** Intra-module cascade (AC §13): edit⟹view, add⟹view+edit, delete⟹view+add+edit.
 * Disabled module flags (e.g. Reports `can_delete`) are skipped by `grant`.
 * Reports: Add does not imply Edit — create/comment vs resolve are separate.
 * Payroll: Add (Add Reward/Deduction, Generate Payroll) and Edit (Revert) are
 * unrelated actions, so Add does not imply Edit there either. */
const INTRA_MODULE_IMPLIES = {
  can_edit: ["can_view"],
  can_add: ["can_view", "can_edit"],
  can_delete: ["can_view", "can_add", "can_edit"],
};

const addImpliesViewOnly = ["can_view"];
const MODULES_WHERE_ADD_EXCLUDES_EDIT = [
  companyPermissionModules.reports,
  companyPermissionModules.payroll,
];

const impliesForModule = (moduleName, sourceFlag) => {
  if (
    sourceFlag === "can_add" &&
    MODULES_WHERE_ADD_EXCLUDES_EDIT.includes(moduleName)
  ) {
    return addImpliesViewOnly;
  }
  return INTRA_MODULE_IMPLIES[sourceFlag] || [];
};

const anyFlagSet = (flags) => FLAGS.some((flag) => flags[flag]);
const canAddOrEdit = (flags) => flags.can_add || flags.can_edit;
const canView = (flags) => flags.can_view;

/** Cross-module dependency sheet. */
export const CROSS_MODULE_RULES = [
  { source: companyPermissionModules.joiningEvents, when: anyFlagSet, target: companyPermissionModules.events, grant: "can_view" },
  { source: companyPermissionModules.manageReports, when: anyFlagSet, target: companyPermissionModules.events, grant: "can_view" },
  { source: companyPermissionModules.reports, when: anyFlagSet, target: companyPermissionModules.events, grant: "can_view" },
  { source: companyPermissionModules.evaluations, when: anyFlagSet, target: companyPermissionModules.joiningEvents, grant: "can_view" },
  { source: companyPermissionModules.evaluations, when: anyFlagSet, target: companyPermissionModules.applicants, grant: "can_view" },
  { source: companyPermissionModules.payroll, when: canView, target: companyPermissionModules.events, grant: "can_view" },
  { source: companyPermissionModules.timesheet, when: canView, target: companyPermissionModules.events, grant: "can_view" },
  { source: companyPermissionModules.heatmap, when: canView, target: companyPermissionModules.events, grant: "can_view" },
  { source: companyPermissionModules.surveyScore, when: canView, target: companyPermissionModules.evaluations, grant: "can_view" },
  { source: companyPermissionModules.applicants, when: canView, target: companyPermissionModules.events, grant: "can_view" },
  { source: companyPermissionModules.events, when: canAddOrEdit, target: companyPermissionModules.employees, grant: "can_view" },
  { source: companyPermissionModules.events, when: canAddOrEdit, target: companyPermissionModules.manageClients, grant: "can_view" },
  { source: companyPermissionModules.events, when: canAddOrEdit, target: companyPermissionModules.applicants, grant: "can_view" },
  { source: companyPermissionModules.employees, when: canAddOrEdit, target: companyPermissionModules.rolesManagement, grant: "can_view" },
];

const emptyFlags = () => ({
  can_view: false,
  can_add: false,
  can_edit: false,
  can_delete: false,
});

const cloneByModule = (permissions) =>
  ALL_MODULES.reduce((acc, module) => {
    acc[module.moduleName] = { ...emptyFlags(), ...permissions[module.moduleName] };
    return acc;
  }, {});

const moduleByName = (moduleName) =>
  ALL_MODULES.find((module) => module.moduleName === moduleName);

/**
 * @param {Object} permissions - explicit map keyed by module_name.
 * @returns {{ effective: Object, forced: Object }} effective renders/saves;
 * forced marks flags set by a rule (checked + disabled in the UI).
 */
export const applyPermissionDependencies = (permissions) => {
  const effective = cloneByModule(permissions);
  const forced = ALL_MODULES.reduce((acc, module) => {
    acc[module.moduleName] = emptyFlags();
    return acc;
  }, {});

  const grant = (moduleName, flag) => {
    if (effective[moduleName][flag]) return false;
    const module = moduleByName(moduleName);
    if (module && !isLevelEnabled(module, flag)) return false;
    effective[moduleName][flag] = true;
    forced[moduleName][flag] = true;
    return true;
  };

  // Monotonic (only ever sets flags true) so a fixed-point loop always
  // terminates — bounded by the total number of flags as a safety guard.
  const maxIterations = ALL_MODULES.length * FLAGS.length;
  let changed = true;
  let iterations = 0;

  while (changed && iterations < maxIterations) {
    changed = false;
    iterations += 1;

    ALL_MODULES.forEach((module) => {
      const flags = effective[module.moduleName];
      Object.keys(INTRA_MODULE_IMPLIES).forEach((sourceFlag) => {
        if (!flags[sourceFlag] || !isLevelEnabled(module, sourceFlag)) return;
        impliesForModule(module.moduleName, sourceFlag).forEach((targetFlag) => {
          if (grant(module.moduleName, targetFlag)) changed = true;
        });
      });
    });

    CROSS_MODULE_RULES.forEach((rule) => {
      const sourceFlags = effective[rule.source];
      if (!sourceFlags || !rule.when(sourceFlags)) return;
      if (grant(rule.target, rule.grant)) changed = true;
    });
  }

  return { effective, forced };
};
